/**
 * Bundles customize add-ons into the main product form cart add.
 * Forwards line-item properties (e.g. VIN) onto the main product line.
 *
 * Products with upsell_product_bundled_with_parent (core charges):
 * - Require acknowledgement checkbox (live-site fee wording as label)
 * - Are always added with the parent when acknowledgement is checked
 * - Share a _bundle_id on parent + child so cart/sidecart treat them as one unit
 */
class CustomizeYourItem extends HTMLElement {
  #onSubmit = this._onSubmit.bind(this);

  connectedCallback() {
    const formId = this.getAttribute("form");
    this.form = formId ? document.getElementById(formId) : null;
    if (!this.form) return;
    this.form.addEventListener("submit", this.#onSubmit, true);
  }

  disconnectedCallback() {
    this.form?.removeEventListener("submit", this.#onSubmit, true);
  }

  _selectedOptionalAddons() {
    return Array.from(this.querySelectorAll('input[type="radio"]:checked'))
      .filter((input) => input.value && input.value !== "none")
      .map((input) => ({
        id: Number(input.value),
        bundledWithParent: false,
        title: input.closest("[data-addon-title]")?.getAttribute("data-addon-title") || "",
      }));
  }

  _requiredBundledAddons() {
    return Array.from(this.querySelectorAll("[data-required-bundled-item]")).map((el) => ({
      id: Number(el.getAttribute("data-variant-id")),
      bundledWithParent: true,
      title: el.getAttribute("data-addon-title") || "",
      acknowledged: Boolean(el.querySelector('input[type="checkbox"]:checked')),
      checkbox: el.querySelector('input[type="checkbox"]'),
    }));
  }

  _lineItemProperties(formData) {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== "") {
        properties[match[1]] = String(value).trim();
      }
    }
    return properties;
  }

  _createBundleId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async _onSubmit(event) {
    const requiredBundled = this._requiredBundledAddons().filter((addon) => addon.id);
    const optionalAddons = this._selectedOptionalAddons();
    const hasRequiredBundled = requiredBundled.length > 0;

    // Only take over submit when we have optional selections and/or required core charges.
    if (!hasRequiredBundled && optionalAddons.length === 0) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (hasRequiredBundled && requiredBundled.some((addon) => !addon.acknowledged)) {
      const firstUnchecked = requiredBundled.find((addon) => !addon.acknowledged)?.checkbox;
      firstUnchecked?.focus();
      this.form.reportValidity();
      return;
    }

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const submitButtons = Array.from(this.form.elements).filter((el) => el.type === "submit");
    submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));

    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    const formData = new FormData(this.form);
    const mainId = formData.get("id");
    const quantity = Number(formData.get("quantity") || 1);
    const properties = this._lineItemProperties(formData);
    // Shared only between parent + upsell_product_bundled_with_parent children.
    const bundleId = hasRequiredBundled ? this._createBundleId() : null;
    const parentTitle = this.getAttribute("data-parent-title") || "";
    const parentProductId = this.getAttribute("data-parent-product-id") || "";
    const addons = [...requiredBundled, ...optionalAddons];

    const mainProperties = { ...properties };
    if (bundleId) {
      mainProperties._bundle_id = bundleId;
      mainProperties._bundle_role = "parent";
      mainProperties._has_bundled_upsell = "true";
      const ackLabels = requiredBundled.map((addon) => addon.title).filter(Boolean);
      if (ackLabels.length) {
        mainProperties["Core charge acknowledged"] = ackLabels.join(", ");
      }
      const bundledTitles = requiredBundled.map((addon) => addon.title).filter(Boolean);
      if (bundledTitles.length) {
        mainProperties.Includes = bundledTitles.join(", ");
      }
    }

    const mainItem = {
      id: Number(mainId),
      quantity,
      properties: mainProperties,
    };

    const items = [mainItem];
    for (const addon of addons) {
      const addonProperties = {};

      if (addon.bundledWithParent && bundleId) {
        addonProperties._bundle_id = bundleId;
        addonProperties._bundle_role = "child";
        addonProperties._bundled_with_parent = "true";
        addonProperties._parent_variant_id = String(mainId);
        addonProperties["Acknowledged"] = "Yes";
        if (parentProductId) {
          addonProperties._parent_product_id = String(parentProductId);
        }
        if (parentTitle) {
          addonProperties["Bundled with"] = parentTitle;
        }
      }

      const addonItem = { id: addon.id, quantity: 1 };
      if (Object.keys(addonProperties).length > 0) {
        addonItem.properties = addonProperties;
      }
      items.push(addonItem);
    }

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          items,
          sections: sectionsToBundle.join(","),
          sections_url: `${Shopify.routes.root}variants/${mainId}`,
        }),
      });

      const responseJson = await response.json();
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));

      if (!response.ok) {
        this.form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: responseJson.description || responseJson.message },
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
        return;
      }

      if (
        window.themeVariables?.settings?.cartType === "page" ||
        window.themeVariables?.settings?.pageType === "cart"
      ) {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      this.form.dispatchEvent(
        new CustomEvent("variant:add", {
          bubbles: true,
          detail: {
            items: responseJson.items || [responseJson],
            cart: cartContent,
          },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "variant:add",
            cart: cartContent,
          },
        })
      );
    } catch (error) {
      submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
      this.form.dispatchEvent(
        new CustomEvent("cart:error", {
          bubbles: true,
          detail: { error: error?.message || "Unable to add items to cart" },
        })
      );
    }
  }
}

if (!window.customElements.get("customize-your-item")) {
  window.customElements.define("customize-your-item", CustomizeYourItem);
}
